import type { CardId } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import type { CardTripState } from '@web/ui/core/hooks';
import { Drawer } from '@web/ui/core/organisms';

import { CardTripColumn } from './card_trip_column';
import { CustomerContactCard } from './customer_contact_card';

export type CardSideColumnProps = Readonly<
  { cardId: CardId; tripState: CardTripState } & (
    | { variant: 'column'; onCloseDrawer?: never }
    | { variant: 'drawer'; onCloseDrawer: () => void }
  )
>;

/**
 * A viagem só entra em cena quando já se sabe que ela existe — em `'loading'`
 * sem `tripKnown` ainda não sabemos se há viagem, e mostrar aí faria o texto
 * de carregando piscar em todo card assumido sem viagem (o request da
 * conversa ainda não voltou para dizer que não há `tripId`).
 */
function tripIsKnown(state: CardTripState): boolean {
  if (state.status === 'none') return false;
  if (state.status === 'loading') return state.tripKnown;
  return true;
}

/**
 * A coluna lateral da tela cheia (aba Conversa): o bloco Cliente em cima e,
 * quando há viagem, o itinerário embaixo. Está sempre presente no card
 * assumido — sem viagem, fica só o Cliente. Em ≥ xl é o `aside` ao lado da
 * conversa; abaixo disso, o mesmo corpo num `Drawer` aberto pelo header.
 */
export function CardSideColumn(props: CardSideColumnProps): React.JSX.Element {
  const body = (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto">
      <CustomerContactCard cardId={props.cardId} />
      {tripIsKnown(props.tripState) ? (
        // `flex-1` aqui (e não só no `.genui2` do `TripColumnBody`) é o que dá
        // altura de verdade pro itinerário: sem ela o `flex-1 overflow-auto` lá
        // dentro fica inerte (o pai encolhe pro conteúdo) e quem rola é a coluna
        // toda; com ela o bloco Cliente fica fixo em cima e só o itinerário rola.
        <div className="flex min-h-0 flex-1 flex-col border-t border-border-soft">
          <CardTripColumn state={props.tripState} />
        </div>
      ) : null}
    </div>
  );
  if (props.variant === 'drawer') {
    return (
      <Drawer open onClose={props.onCloseDrawer} ariaLabel={t('card.side.columnAria')}>
        {body}
      </Drawer>
    );
  }
  return (
    <aside
      aria-label={t('card.side.columnAria')}
      className="flex w-trip-col-w min-h-0 shrink-0 flex-col overflow-hidden border-l border-border-soft bg-surface"
      data-testid="card-side-column"
    >
      {body}
    </aside>
  );
}
